// 고객사 기준 정보 — «기준 변경이력» 카드(항목 select · 검색 · 최신순 표). 화면·엑셀 가져오기·인수인계로 바뀐 기준이 다 남는다.
// 검색은 입력이 300ms 멈춘 뒤 요청한다 · reloadKey 가 바뀌면(기준 수정·종료·복구·명부 연결 뒤) 다시 읽는다.
import { useEffect, useState } from 'react';
import { workCustomersAPI } from '../../api/client';
import { SearchInput } from '../../components/ui';
import { fmtKstDateTime } from '../../utils/kstTime';
import { COLORS, card, input } from '../../utils/uiStyles';
import { dash, errorText } from './workCustomerLabels';

export const HISTORY_COLUMNS = ['변경 시각', '항목', '변경 전', '변경 후', '변경자', '사유', '출처'];
export const SEARCH_DEBOUNCE_MS = 300;

const th = { textAlign: 'left', padding: '7px 8px', fontSize: '12px', fontWeight: 500, color: COLORS.textSub, borderBottom: `1px solid ${COLORS.border}`, whiteSpace: 'nowrap' };
const td = { padding: '7px 8px', fontSize: '13px', color: COLORS.text, borderBottom: `1px solid ${COLORS.borderSoft}`, verticalAlign: 'top', wordBreak: 'break-word' };

export default function HistoryCard({ customerId, customerName, reloadKey }) {
  const [category, setCategory] = useState('');
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  // 고객사를 바꾸면 거름 조건을 비운다(항목 목록이 고객사마다 다르다).
  useEffect(() => {
    setCategory('');
    setQuery('');
    setDebounced('');
  }, [customerId]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (customerId == null) { setData(null); return undefined; }
    let alive = true;
    const params = {};
    if (category) params.category = category;
    if (debounced) params.q = debounced;
    setError('');
    workCustomersAPI.history(customerId, params)
      .then((res) => { if (alive) setData(res && res.data ? res.data : { items: [], categories: [] }); })
      .catch((e) => { if (alive) { setData(null); setError(errorText(e, '변경이력을 불러오지 못했습니다')); } });
    return () => { alive = false; };
  }, [customerId, category, debounced, reloadKey]);

  const items = data && Array.isArray(data.items) ? data.items : [];
  const categories = data && Array.isArray(data.categories) ? data.categories : [];

  return (
    <section data-testid="work-customer-history" aria-label="기준 변경이력" style={{ ...card, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: COLORS.text }}>기준 변경이력</h2>
        {customerName && <span style={{ marginLeft: 'auto', fontSize: '12px', color: COLORS.textSub }}>{customerName}</span>}
      </div>
      {customerId == null ? (
        <p style={{ margin: 0, fontSize: '12px', color: COLORS.textMuted }}>고객사를 고르면 변경이력이 보입니다.</p>
      ) : (
        <>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '8px' }}>
            <select aria-label="항목" value={category} onChange={(e) => setCategory(e.target.value)}
              style={{ ...input, width: 'auto', minWidth: '130px', padding: '5px 8px', fontSize: '12px' }}>
              <option value="">전체 항목</option>
              {categories.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
            </select>
            <SearchInput value={query} onChange={(e) => setQuery(e.target.value)} placeholder="세부 항목·값·사유·변경자 검색" width="260px" />
          </div>
          {error && <div role="alert" style={{ fontSize: '12px', color: COLORS.dangerText, marginBottom: '8px' }}>{error}</div>}
          <div style={{ overflowX: 'auto' }}>
            <table data-testid="work-customer-history-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>{HISTORY_COLUMNS.map((h) => <th key={h} scope="col" style={th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {!error && data && items.length === 0 && (
                  <tr><td colSpan={HISTORY_COLUMNS.length} style={{ ...td, textAlign: 'center', color: COLORS.textMuted }}>변경이력이 없습니다.</td></tr>
                )}
                {items.map((h) => (
                  <tr key={h.id} data-testid="work-customer-history-row">
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtKstDateTime(h.changed_at) || '—'}</td>
                    <td style={td}>{dash(h.item)}</td>
                    <td style={td}>{dash(h.before)}</td>
                    <td style={td}>{dash(h.after)}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{dash(h.changed_by_name)}</td>
                    <td style={td}>{dash(h.reason)}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{dash(h.source)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
